import { Button, Card, CardContent } from '@/components/ui';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import type { AccountingProvider } from '../../../../../services/accountingIntegration';
import { AccountingProviderLogo } from './AccountingProviderLogo';
import { PROVIDER_LABELS } from './accountingIntegrationUi';

const PROVIDERS: {
  id: AccountingProvider;
  description: string;
}[] = [
  {
    id: 'xero',
    description: 'Sync lesson payments, refunds, and payouts to your Xero organisation.',
  },
  {
    id: 'quickbooks',
    description: 'Sync lesson payments, refunds, and payouts to QuickBooks Online.',
  },
];

interface Props {
  connectingProvider: AccountingProvider | null;
  onConnect: (provider: AccountingProvider) => void;
}

export function AccountingProviderPicker({ connectingProvider, onConnect }: Props) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {PROVIDERS.map((provider) => {
        const busy = connectingProvider === provider.id;
        const disabled = connectingProvider != null;

        return (
          <Card
            key={provider.id}
            className={cn(
              'border-2 transition-colors',
              busy
                ? 'border-primary-500 ring-1 ring-primary-500/30'
                : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
            )}
          >
            <CardContent className="flex h-full flex-col gap-4 p-5">
              <div className="flex items-start gap-3">
                <AccountingProviderLogo provider={provider.id} />
                <div className="min-w-0">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                    {PROVIDER_LABELS[provider.id]}
                  </h4>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {provider.description}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                className="mt-auto w-full sm:w-auto"
                onClick={() => onConnect(provider.id)}
                disabled={disabled}
                aria-busy={busy}
              >
                {busy ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
                    Redirecting…
                  </>
                ) : (
                  `Connect ${PROVIDER_LABELS[provider.id]}`
                )}
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
